import React, { useEffect, useState, useRef } from "react";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  IconButton,
  MenuItem,
  Popover,
  Modal,
  Select,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import MatricIdsListModal from "../../components/Model/MatricIdsListModal";
import { CiEdit } from "react-icons/ci";
import { RxPlus, RxCross1 } from "react-icons/rx";
import { TbAlertHexagonFilled } from "react-icons/tb";
import { MdTransitEnterexit, MdClose, MdPhoneAndroid } from "react-icons/md";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { HttpService } from "../../service/HttpService";
import { toast } from "react-toastify";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";

import { GoTrash } from "react-icons/go";

const Trimming = () => {
  const httpService = new HttpService();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [cultivars, setCultivars] = useState([]);
  const [importTags, setImportTags] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Move to Inventory modal state
  const [openInventoryModal, setOpenInventoryModal] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const selectedPkgForAction = packages.find((p) => p.id === selectedActionId);

  // Matric modal states
  const [matricModalOpen, setMatricModalOpen] = useState(false);
  const [selectedMatricRow, setSelectedMatricRow] = useState(null);

  // Move to App modal states
  const [employees, setEmployees] = useState([]);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [assignStartDate, setAssignStartDate] = useState(dayjs());
  const [assignEndDate, setAssignEndDate] = useState(dayjs());
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const handleOpenMatricModal = (row) => {
    setSelectedMatricRow(row);
    setMatricModalOpen(true);
  };

  const editingIdRef = useRef(editingId);
  const editValuesRef = useRef(editValues);

  useEffect(() => {
    editingIdRef.current = editingId;
    editValuesRef.current = editValues;
  }, [editingId, editValues]);

  const fetchTrimmingData = async () => {
    setLoading(true);
    try {
      const res = await httpService.get("harvest-trimming");
      setPackages(res?.data || []);
    } catch (e) {
      console.error(e);
      setPackages([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTrimmingData();
    const fetchEmployees = async () => {
      try {
        const res = await httpService.get("employee/get_employees?sortBy=name&sortOrder=ASC");
        setEmployees(res?.data?.data || res?.data || []);
      } catch (e) {
        console.error("Error fetching employees:", e);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchImportTags = async () => {
      try {
        const res = await httpService.get("harvestimport?assigned=false");
        const list = res?.data?.data || res?.data || [];
        setImportTags(list);
      } catch (e) {
        console.error("Error fetching import tags:", e);
      }
    };
    fetchImportTags();
  }, []);

  // Handle click outside to auto-save
  useEffect(() => {
    const handleClickOutside = (event) => {
      const currentId = editingIdRef.current;
      const currentValues = editValuesRef.current;

      const isPopover = !!event.target.closest(
        ".MuiPopover-root, .MuiDialog-root, .MuiPickersPopper-root, .MuiPickersLayout-root, .MuiAutocomplete-popper, .MuiPopper-root",
      );
      if (isPopover) return;
      if (!document.body.contains(event.target)) return;

      if (currentId && currentValues && Object.keys(currentValues).length > 0) {
        const isInsideRow = event.target.closest(
          `.MuiDataGrid-row[data-id="${currentId}"], input, textarea, select, .MuiSelect-select`,
        );
        if (!isInsideRow) {
          const isScrollbarClick =
            !!event.target.closest(".MuiDataGrid-virtualScroller") &&
            !event.target.closest(".MuiDataGrid-row");
          if (isScrollbarClick) return;

          const isEmpty =
            !currentValues.harvest_date &&
            !currentValues.cultivar_id &&
            !currentValues.room_id &&
            !currentValues.source_package_tag;
          if (isEmpty) {
            handleCancelRow();
          } else {
            handleSaveRow(currentValues);
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const handleAddRow = () => {
    const newRow = {
      id: `new_${Date.now()}`,
      week_of_harvest: "",
      harvest_date: null,
      cultivar_id: "",
      cultivar_name: "",
      room_id: "",
      room_name: "",
      plants: "",
      source_package_tag: "",
      child_package_tag: "",
      bulk_smalls_trim: "",
      weight: "",
      waste: "",
      isNew: true,
    };
    setPackages([newRow, ...packages]);
    setEditingId(newRow.id);
    setEditValues(newRow);
  };

  const handleEditRow = async (row) => {
    setEditingId(row.id);
    setEditValues({
      ...row,
    });
    handleMenuClose();

    if (row.harvest_date) {
      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${dayjs(row.harvest_date).format("YYYY-MM-DD")}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
        } else {
          setRooms([]);
          setCultivars([]);
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
      }
    } else {
      setRooms([]);
      setCultivars([]);
    }
  };

  const handleCancelRow = () => {
    setPackages((prev) => prev.filter((p) => !String(p.id).startsWith("new_")));
    setEditingId(null);
    setEditValues({});
  };

  const handleSaveRow = async (valuesToSave) => {
    const currentValues = valuesToSave || editValues;
    const currentId = editingIdRef.current || editingId;
    if (!currentValues || Object.keys(currentValues).length === 0) return;

    const isEmpty =
      !currentValues.harvest_date &&
      !currentValues.cultivar_id &&
      !currentValues.room_id &&
      !currentValues.source_package_tag;
    if (isEmpty) {
      handleCancelRow();
      return;
    }

    setLoading(true);
    try {
      const isNew = String(currentId).startsWith("new_");
      const payload = {
        week_of_harvest: parseInt(currentValues.week_of_harvest) || 0,
        harvest_date: currentValues.harvest_date
          ? dayjs(currentValues.harvest_date).format("YYYY-MM-DD")
          : null,
        cultivar_id: parseInt(currentValues.cultivar_id) || null,
        room_id: parseInt(currentValues.room_id) || null,
        plants: parseInt(currentValues.plants) || 0,
        source_package_tag: parseInt(currentValues.source_package_tag) || null,
        child_package_tag: parseInt(currentValues.child_package_tag) || null,
        bulk_smalls_trim: currentValues.bulk_smalls_trim || "",
        weight: parseFloat(currentValues.weight) || 0,
        waste: parseFloat(currentValues.waste) || 0,
      };

      if (isNew) {
        await httpService.post("harvest-trimming", payload);
        toast.success("Package added successfully", { position: "top-right" });
      } else {
        await httpService.put(`harvest-trimming/${currentId}`, payload);
        toast.success("Package updated successfully", {
          position: "top-right",
        });
      }
      setEditingId(null);
      setEditValues({});
      fetchTrimmingData();
    } catch (error) {
      console.error(error);
      toast.error(
        String(currentId).startsWith("new_")
          ? "Failed to save package"
          : "Failed to update package",
        { position: "top-right" },
      );
    }
    setLoading(false);
  };

  const handleFieldChange = async (field, value) => {
    let updatedValues = { ...editValues, [field]: value };

    if (field === "harvest_date" && value) {
      const getWeekOfYear = (date) => {
        const d = new Date(date);
        const yearStart = new Date(d.getFullYear(), 0, 1);
        return Math.ceil(
          ((d - yearStart) / 86400000 + yearStart.getDay() + 1) / 7,
        );
      };
      const weekNumber = getWeekOfYear(value);
      updatedValues = { ...updatedValues, week_of_harvest: weekNumber };

      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${value}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
          updatedValues = {
            ...updatedValues,
            room_id: "",
            cultivar_id: "",
            plants: "",
          };
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
        setRooms([]);
        setCultivars([]);
      }
    }

    if (
      (field === "cultivar_id" || field === "room_id") &&
      updatedValues.cultivar_id &&
      updatedValues.room_id &&
      updatedValues.harvest_date
    ) {
      try {
        const response = await httpService.get(
          `harvest/plant-count?plantId=${updatedValues.cultivar_id}&roomId=${updatedValues.room_id}&harvestedDate=${updatedValues.harvest_date}`,
        );
        const count =
          response?.data?.count ?? response?.data?.data ?? response?.data;
        if (count !== undefined && count !== null) {
          updatedValues = { ...updatedValues, plants: parseInt(count) || 0 };
        }
      } catch (error) {
        console.error("Error fetching plant count:", error);
      }
    }

    setEditValues(updatedValues);
  };
  const handleDuplicateRow = async (row) => {
    const newRow = {
      id: `new_${Date.now()}`,
      week_of_harvest: row.week_of_harvest || "",
      harvest_date: row.harvest_date
        ? dayjs(row.harvest_date).format("YYYY-MM-DD")
        : null,
      cultivar_id: row.cultivar_id || "",
      cultivar_name: row.cultivar_name || "",
      room_id: row.room_id || "",
      room_name: row.room_name || "",
      plants: row.plants || "",
      source_package_tag: row.source_package_tag || "",
      source_package_tag_name: row.source_package_tag_name || "",
      child_package_tag: "",
      child_package_tag_name: "",
      bulk_smalls_trim: row.bulk_smalls_trim || "",
      weight: row.weight || "",
      waste: row.waste || "",
      isNew: true,
    };

    setPackages([newRow, ...packages]);
    setEditingId(newRow.id);
    setEditValues(newRow);

    if (row.harvest_date) {
      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${dayjs(row.harvest_date).format("YYYY-MM-DD")}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
        } else {
          setRooms([]);
          setCultivars([]);
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
      }
    }
  };

  const handleMoveToInventory = (id) => {
    setSelectedActionId(id);
    setOpenInventoryModal(true);
    handleMenuClose();
  };

  const confirmMoveToInventory = async () => {
    setLoading(true);
    try {
      const pkg = packages.find((p) => p.id === selectedActionId);
      if (pkg) {
        const payload = {
          week_of_harvest: parseInt(pkg.week_of_harvest) || 0,
          harvest_date: pkg.harvest_date
            ? dayjs(pkg.harvest_date).format("YYYY-MM-DD")
            : null,
          cultivar_id: parseInt(pkg.cultivar_id) || null,
          room_id: parseInt(pkg.room_id) || null,
          plants: parseInt(pkg.plants) || 0,
          source_package_tag: parseInt(pkg.source_package_tag) || null,
          child_package_tag: parseInt(pkg.child_package_tag) || null,
          bulk_smalls_trim: pkg.bulk_smalls_trim || "",
          weight: parseFloat(pkg.weight) || 0,
          location: "Trim Room",
        };
        await httpService.post("harvest-inventory", payload);
        await httpService.delete(`harvest-trimming/delete/${pkg.id}`);
        toast.success("Moved package to Inventory successfully", {
          position: "top-right",
        });
        fetchTrimmingData();
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to move package to Inventory", {
        position: "top-right",
      });
    }
    setLoading(false);
    setOpenInventoryModal(false);
    setSelectedActionId(null);
  };

  const handleMoveToApp = (id) => {
    setSelectedActionId(id);
    setSelectedRowIds([]);
    setSelectedEmployeeId("");
    setAssignStartDate(dayjs());
    setAssignEndDate(dayjs());
    setOpenAssignModal(true);
    handleMenuClose();
  };

  const confirmAssignEmployee = async () => {
    if (!selectedEmployeeId) {
      toast.error("Please select an employee name", { position: "top-right" });
      return;
    }
    if (!assignStartDate || !assignEndDate) {
      toast.error("Please select both Start and End Dates", { position: "top-right" });
      return;
    }
    if (selectedRowIds.length === 0) {
      toast.error("Please select at least one package to assign", { position: "top-right" });
      return;
    }

    setLoading(true);
    try {
      const trimmingRows = selectedRowIds.map((id, index) => ({
        id: parseInt(id),
        priority: index + 1
      }));

      const payload = {
        employee_id: [parseInt(selectedEmployeeId)],
        start_date: assignStartDate.format("YYYY-MM-DD"),
        end_date: assignEndDate.format("YYYY-MM-DD"),
        trimming_rows: trimmingRows
      };

      await httpService.post("harvest-trimming/assign-employee", payload);

      toast.success("Trimming packages assigned successfully", {
        position: "top-right",
      });
      setOpenAssignModal(false);
      setSelectedRowIds([]);
      setSelectedEmployeeId("");
      fetchTrimmingData();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Failed to assign employee to trimming", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRow = async () => {
    if (!selectedActionId) return;
    setLoading(true);
    try {
      await httpService.delete(`harvest-trimming/delete/${selectedActionId}`);
      toast.success("Package deleted successfully", { position: "top-right" });
      setDeleteModalOpen(false);
      setSelectedActionId(null);
      fetchTrimmingData();
    } catch (error) {
      toast.error("Failed to delete package", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          {editingId === params.row.id ? (
            <IconButton onClick={handleCancelRow} color="error">
              <MdClose />
            </IconButton>
          ) : (
            <>
              <IconButton onClick={(e) => handleMenuOpen(e, params.row.id)}>
                <GridMoreVertIcon />
              </IconButton>
              {anchorEl && menuRowId === params.row.id && (
                <Popover
                  className="popup-menu"
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={() => handleEditRow(params.row)}
                    className="menu-list-items"
                  >
                    <div className="menu-list-items-text">Edit</div>
                    <CiEdit style={{ cursor: "pointer" }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleMoveToInventory(params.row.id)}
                    className="menu-list-items"
                  >
                    <div className="menu-list-items-text">
                      Move To Inventory
                    </div>
                    <MdTransitEnterexit style={{ cursor: "pointer" }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleMoveToApp(params.row.id)}
                    className="menu-list-items"
                  >
                    <div className="menu-list-items-text">
                      Move to app
                    </div>
                    <MdPhoneAndroid style={{ cursor: "pointer" }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSelectedActionId(params.row.id);
                      setDeleteModalOpen(true);
                      handleMenuClose();
                    }}
                    className="menu-list-items"
                  >
                    <div className="menu-list-items-text">Delete</div>
                    <GoTrash style={{ cursor: "pointer" }} />
                  </MenuItem>
                </Popover>
              )}
            </>
          )}
        </>
      ),
    },
    {
      field: "week_of_harvest",
      headerName: "Week of Harvest",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        /*
        editingId === params.row.id ? (
          <input
            type="number"
            value={editValues.week_of_harvest || ""}
            onChange={(e) =>
              handleFieldChange("week_of_harvest", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
        */
        params.value || "-",
        /*
        )
        */
    },
    {
      field: "harvest_date",
      headerName: "Harvest Date",
      flex: 1,
      minWidth: 200,
      renderCell: (params) =>
        /*
        editingId === params.row.id ? (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                editValues.harvest_date ? dayjs(editValues.harvest_date) : null
              }
              onChange={(newValue) => {
                const dateValue = newValue ? newValue.format("YYYY-MM-DD") : "";
                handleFieldChange("harvest_date", dateValue);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
              disableFuture
            />
          </LocalizationProvider>
        ) : (
        */
        params.value ? dayjs(params.value).format("MM-DD-YYYY") : "-",
        /*
        )
        */
    },
    {
      field: "cultivar_name",
      headerName: "Cultivar",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        /*
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.cultivar_id || ""}
              onChange={(e) => handleFieldChange("cultivar_id", e.target.value)}
              native
            >
              <option value="">Select Cultivar</option>
              {cultivars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </FormControl>
        ) : (
        */
        params.value || "-",
        /*
        )
        */
    },
    {
      field: "room_name",
      headerName: "Room",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        /*
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.room_id || ""}
              onChange={(e) => handleFieldChange("room_id", e.target.value)}
              native
            >
              <option value="">Select Room</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </FormControl>
        ) : (
        */
        params.value || "-",
        /*
        )
        */
    },
    {
      field: "plants",
      headerName: "Plants",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        /*
        editingId === params.row.id ? (
          <input
            type="number"
            value={editValues.plants || ""}
            onChange={(e) => handleFieldChange("plants", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
        */
        (
          <span
            style={{
              textDecoration: "underline",
              color: "#427fcfff",
              cursor: "pointer",
            }}
            onClick={() => handleOpenMatricModal(params.row)}
          >
            {params.value || 0}
          </span>
        ),
        /*
        )
        */
    },
    {
      field: "source_package_tag",
      headerName: "Source Package Tag",
      flex: 1,
      minWidth: 350,
      renderCell: (params) =>
        /*
        editingId === params.row.id ? (
          <Autocomplete
            id="source-package-tag-autocomplete"
            options={importTags}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option?.tag || "";
            }}
            value={
              importTags.find(
                (opt) => opt.id === editValues.source_package_tag,
              ) ||
              (editValues.source_package_tag_name
                ? {
                    id: editValues.source_package_tag,
                    tag: editValues.source_package_tag_name,
                  }
                : null)
            }
            onChange={(event, newValue) => {
              setEditValues((prev) => ({
                ...prev,
                source_package_tag: newValue ? newValue.id : "",
                source_package_tag_name: newValue ? newValue.tag : "",
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search or Select Tag"
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                padding: "2px 8px",
              },
            }}
          />
        ) : (
        */
        params.row.source_package_tag_name || params.value || "-",
        /*
        )
        */
    },
    {
      field: "child_package_tag",
      headerName: "Child Package Tag",
      flex: 1,
      minWidth: 350,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <Autocomplete
            id="child-package-tag-autocomplete"
            options={importTags}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option?.tag || "";
            }}
            value={
              importTags.find(
                (opt) => opt.id === editValues.child_package_tag,
              ) ||
              (editValues.child_package_tag_name
                ? {
                    id: editValues.child_package_tag,
                    tag: editValues.child_package_tag_name,
                  }
                : null)
            }
            onChange={(event, newValue) => {
              setEditValues((prev) => ({
                ...prev,
                child_package_tag: newValue ? newValue.id : "",
                child_package_tag_name: newValue ? newValue.tag : "",
              }));
            }}
            renderInput={(paramsInput) => (
              <TextField
                {...paramsInput}
                placeholder="Search or Select Tag"
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                padding: "2px 8px",
              },
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>
              {params.row.child_package_tag_name || params.value || "-"}
            </span>
            <IconButton
              size="small"
              onClick={() => handleDuplicateRow(params.row)}
              title="Duplicate package (excluding child tag)"
              sx={{
                backgroundColor: "#00A272",
                color: "#FFFFFF",
                borderRadius: "4px",
                width: "28px",
                height: "28px",
                padding: "4px",
                "&:hover": {
                  backgroundColor: "#00875F",
                },
              }}
            >
              <RxPlus />
            </IconButton>
          </div>
        ),
    },
    {
      field: "bulk_smalls_trim",
      headerName: "Bulk/Smalls/Trim",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.bulk_smalls_trim || ""}
              onChange={(e) =>
                handleFieldChange("bulk_smalls_trim", e.target.value)
              }
              native
            >
              <option value="">Select Type</option>
              <option value="Bulk">Bulk</option>
              <option value="Smalls">Smalls</option>
              <option value="Trim">Trim</option>
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "weight",
      headerName: "Weight",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="number"
            value={editValues.weight || ""}
            onChange={(e) => handleFieldChange("weight", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : params.value ? (
          `${params.value} lbs`
        ) : (
          "-"
        ),
    },
    {
      field: "waste",
      headerName: "Waste",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="number"
            value={editValues.waste || ""}
            onChange={(e) => handleFieldChange("waste", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : params.value ? (
          `${params.value} lbs`
        ) : (
          "-"
        ),
    },
    {
      field: "assign_to",
      headerName: "Assign to App",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="string"
            value={editValues.assign_to || ""}
            onChange={(e) => handleFieldChange("assign_to", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          // `${params.value}`
           <span
              style={{
                backgroundColor: params.row.schedule_task_id ? "#E6F6F1" : "#F3F4F6",
                color: params.row.schedule_task_id ? "#00A272" : "#4B5563",
                padding: "4px 8px",
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "12px",
                display: "inline-block",
              }}
            >
              {params.row.schedule_task_id ? "Yes" : "No"}
            </span>
        ),
    },
  ];

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="harvest">
        <div className="page-title">
          <div className="title-wrap"></div>
          {/* <div className="button-wrap mt-4">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddRow}
          >
            <RxPlus /> Add Packages
          </button>
        </div> */}
        </div>

        <div className="frozen-table-wrapper" style={{ width: "100%" }}>
          {/* Fake frozen header */}
          <div className="fake-frozen-header">
            <div
              className="fake-header-cell"
              style={{
                width: 100,
                backgroundColor: "#e7e7ff",
                fontFamily: "inherit",
                justifyContent: "center",
              }}
            >
              Action
            </div>
          </div>
          <DataGrid
            rows={packages}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            getRowClassName={(params) =>
              params.row.schedule_task_id ? "assigned-row" : ""
            }
            sx={{
              border: "1px solid #E0E0E0",
              [`& .${gridClasses.cell}`]: { py: 1 },
              "& .assigned-row": {
                backgroundColor: "#E6F6F1 !important",
              },
              "& .assigned-row .MuiDataGrid-cell[data-field='action']": {
                backgroundColor: "#E6F6F1 !important",
              },
              "& .assigned-row:hover": {
                backgroundColor: "#d5f0e7 !important",
              },
              "& .assigned-row:hover .MuiDataGrid-cell[data-field='action']": {
                backgroundColor: "#d5f0e7 !important",
              },
              "& .MuiDataGrid-cell[data-field='action']": {
                position: "sticky !important",
                left: "0 !important",
                zIndex: "2 !important",
                backgroundColor: "#fff !important",
                borderRight: "1px solid #E0E0E0 !important",
                boxShadow: "2px 0 5px -2px rgba(0, 0, 0, 0.2) !important",
              },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='action']": {
                backgroundColor: "#F5F5F7 !important",
              },
              "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field='action']": {
                backgroundColor: "#E6F6F1 !important",
              },
            }}
            hideFooter
            showCellVerticalBorder
            showColumnVerticalBorder
          />
        </div>

        {/* Move to Inventory Modal */}
        <Modal
          open={openInventoryModal}
          onClose={() => setOpenInventoryModal(false)}
        >
          <div className="modal_block">
            <div className="modal_header">
              <div className="modal_title_wrapper">
                <h2 className="modal_title">Move to Inventory</h2>
              </div>
              <div className="cancel-btn-wrap">
                <RxCross1 onClick={() => setOpenInventoryModal(false)} />
              </div>
            </div>
            <div className="modal_content">
              <div className="modal_content_inner">
                <div>
                  <TbAlertHexagonFilled />
                </div>
                <div>
                  <p>
                    {selectedPkgForAction &&
                    !selectedPkgForAction.child_package_tag
                      ? "There is no Child Package Tag assigned to this record. Do you still want to move it to Inventory?"
                      : "Are you sure you want to move this package to Inventory?"}
                  </p>
                </div>
              </div>
            </div>
            <div className="modal_footer">
              <button
                onClick={() => setOpenInventoryModal(false)}
                className="btn btn-border"
              >
                {selectedPkgForAction && !selectedPkgForAction.child_package_tag
                  ? "Cancel"
                  : "No"}
              </button>
              <button
                onClick={confirmMoveToInventory}
                className="btn btn-primary"
              >
                {selectedPkgForAction && !selectedPkgForAction.child_package_tag
                  ? "Move"
                  : "Yes"}
              </button>
            </div>
          </div>
        </Modal>
        {/* Delete Modal */}
        <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
          <div className="modal_block">
            <div className="modal_header">
              <div className="modal_title_wrapper">
                <h2 className="modal_title">Are you Sure?</h2>
              </div>
              <div className="cancel-btn-wrap">
                <RxCross1 onClick={() => setDeleteModalOpen(false)} />
              </div>
            </div>
            <div className="modal_content">
              <div className="modal_content_inner">
                <div>
                  <TbAlertHexagonFilled />
                </div>
                <div>
                  <p>Do you want to delete?</p>
                </div>
              </div>
            </div>
            <div className="modal_footer">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="btn btn-border"
              >
                No
              </button>
              <button onClick={handleDeleteRow} className="btn btn-primary">
                Yes
              </button>
            </div>
          </div>
        </Modal>
        {/* Assign To Employee Modal */}
        <Modal
          open={openAssignModal}
          onClose={() => setOpenAssignModal(false)}
        >
          <div className="modal_block" style={{ width: "90%", maxWidth: 1000, boxSizing: "border-box" }}>
            <div className="modal_header">
              <div className="modal_title_wrapper">
                <h2 className="modal_title">Assign To Employee</h2>
              </div>
              <div className="cancel-btn-wrap">
                <RxCross1 onClick={() => setOpenAssignModal(false)} style={{ cursor: "pointer" }} />
              </div>
            </div>
            <div className="modal_content" style={{ padding: "20px 24px" }}>
              {/* Employee Selection and Date Pickers */}
              <div className="row" style={{ marginBottom: "20px" }}>
                <div className="col-md-12" style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", marginBottom: "6px", fontWeight: 500, fontSize: "14px" }}>Employee</label>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedEmployeeId}
                      onChange={(e) => setSelectedEmployeeId(e.target.value)}
                      displayEmpty
                      sx={{
                        backgroundColor: "#FFF",
                        borderRadius: "4px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E0E0E0"
                        }
                      }}
                    >
                      <MenuItem value="">
                        <span style={{ color: "#aaa" }}>Select Employee Name</span>
                      </MenuItem>
                      {employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {emp.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className="row" style={{ marginBottom: "20px", display: "flex", gap: "15px" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: "6px", fontWeight: 500, fontSize: "14px" }}>Start Date</label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={assignStartDate}
                      onChange={(newValue) => setAssignStartDate(newValue)}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "4px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0"
                            }
                          }
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: "6px", fontWeight: 500, fontSize: "14px" }}>End Date</label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={assignEndDate}
                      onChange={(newValue) => setAssignEndDate(newValue)}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "4px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0"
                            }
                          }
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>

              {/* Table displaying Trimming Packages */}
              <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto", overflowX: "auto", border: "1px solid #E0E0E0", borderRadius: "4px" }}>
                <table style={{ width: "100%", minWidth: "1000px", borderCollapse: "collapse", textAlign: "left", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#F9FAFB", borderBottom: "1px solid #E0E0E0" }}>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Select & Priority</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Week of Harvest</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Harvest Date</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Cultiver</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Room</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Plants</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Source Package Tag</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Child Package Tag</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Bulk/Smalls/Trim</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Weight</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Waste</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Assign to App</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packages
                      .filter((p) => !String(p.id).startsWith("new_"))
                      .map((row) => {
                        const isSelected = selectedRowIds.includes(row.id);
                        const priorityIndex = selectedRowIds.indexOf(row.id);
                        const displayPriority = priorityIndex !== -1 ? priorityIndex + 1 : null;
                        const isAssigned = !!row.schedule_task_id;

                        const handleCheckboxChange = () => {
                          if (isAssigned) return;
                          if (isSelected) {
                            setSelectedRowIds(selectedRowIds.filter((id) => id !== row.id));
                          } else {
                            setSelectedRowIds([...selectedRowIds, row.id]);
                          }
                        };

                        return (
                          <tr
                            key={row.id}
                            style={{
                              borderBottom: "1px solid #F3F4F6",
                              borderLeft: isSelected ? "4px solid #00A272" : "4px solid transparent",
                              backgroundColor: (isSelected || isAssigned) ? "#E6F6F1" : "transparent",
                              transition: "all 0.2s ease"
                            }}
                          >
                            <td style={{ padding: "8px 12px" }}>
                              <div style={{ display: "flex", alignItems: "center" }}>
                                {isAssigned ? (
                                  <div
                                    style={{
                                      border: "2px solid #D1D5DB",
                                      borderRadius: "4px",
                                      width: "22px",
                                      height: "22px",
                                      cursor: "not-allowed",
                                      backgroundColor: "#E5E7EB"
                                    }}
                                  />
                                ) : isSelected ? (
                                  <div
                                    onClick={handleCheckboxChange}
                                    style={{
                                      backgroundColor: "#00A272",
                                      color: "#FFF",
                                      borderRadius: "4px",
                                      width: "22px",
                                      height: "22px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)"
                                    }}
                                  >
                                    {displayPriority}
                                  </div>
                                ) : (
                                  <div
                                    onClick={handleCheckboxChange}
                                    style={{
                                      border: "2px solid #D1D5DB",
                                      borderRadius: "4px",
                                      width: "22px",
                                      height: "22px",
                                      cursor: "pointer",
                                      backgroundColor: "#FFF"
                                    }}
                                  />
                                )}
                              </div>
                            </td>
                            <td style={{ padding: "8px 12px", color: "#4B5563" }}>{row.week_of_harvest || "-"}</td>
                            <td style={{ padding: "8px 12px", color: "#4B5563" }}>
                              {row.harvest_date ? dayjs(row.harvest_date).format("MM-DD-YYYY") : "-"}
                            </td>
                            <td style={{ padding: "8px 12px", color: "#111827", fontWeight: 500 }}>{row.cultivar_name || "-"}</td>
                            <td style={{ padding: "8px 12px", color: "#4B5563" }}>{row.room_name || "-"}</td>
                            <td style={{ padding: "8px 12px", color: "#4B5563" }}>{row.plants || 0}</td>
                            <td style={{ padding: "8px 12px", color: "#4B5563" }}>{row.source_package_tag_name || "-"}</td>
                            <td style={{ padding: "8px 12px", color: "#4B5563" }}>{row.child_package_tag_name || "-"}</td>
                            <td style={{ padding: "8px 12px", color: "#4B5563" }}>{row.bulk_smalls_trim || "-"}</td>
                            <td style={{ padding: "8px 12px", color: "#4B5563" }}>{row.weight || "-"}</td>
                            <td style={{ padding: "8px 12px", color: "#4B5563" }}>{row.waste || "-"}</td>
                            <td style={{ padding: "8px 12px" }}>
                              <span
                                style={{
                                  backgroundColor: row.schedule_task_id ? "#E6F6F1" : "#F3F4F6",
                                  color: row.schedule_task_id ? "#00A272" : "#4B5563",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontWeight: 600,
                                  fontSize: "12px",
                                  display: "inline-block",
                                }}
                              >
                                {row.schedule_task_id ? "Yes" : "No"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal_footer" style={{ borderTop: "1px solid #F3F4F6", padding: "16px 24px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                onClick={() => setOpenAssignModal(false)}
                className="btn btn-border"
                style={{
                  padding: "8px 20px",
                  borderRadius: "6px",
                  fontWeight: 500,
                  fontSize: "14px",
                  border: "1px solid #D1D5DB",
                  backgroundColor: "#FFF",
                  color: "#374151",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmAssignEmployee}
                className="btn btn-primary"
                style={{
                  padding: "8px 24px",
                  borderRadius: "6px",
                  fontWeight: 500,
                  fontSize: "14px",
                  backgroundColor: "#00A272",
                  border: "none",
                  color: "#FFF",
                  cursor: "pointer"
                }}
              >
                Save
              </button>
            </div>
          </div>
        </Modal>
        <MatricIdsListModal
          open={matricModalOpen}
          handleClose={() => {
            setMatricModalOpen(false);
            setSelectedMatricRow(null);
          }}
          row={selectedMatricRow}
        />
      </div>
    </>
  );
};

export default Trimming;
